<template>
  <div class="view-job-container">
    <!-- Back Button -->
    <v-btn icon class="back-button" @click="openBackConfirmation">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Job Details -->
    <div v-if="job">
      <v-col cols="12" md="12" lg="10" xl="8" class="pa-2">
        <v-row class="my-4" style="margin-left: 800px;">
          <h2 class="red--text font-weight-bold montserrat-title">
            {{ job.jobName }} - Job #{{ job.jobNumber }}
          </h2>
        </v-row>
      </v-col>

      <v-card class="pa-6 add-job-card">
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <!-- Job Details Section -->
            <h3 class="section-title montserrat-title">Job Details</h3>
            <v-row dense>
              <!-- Job Number -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.jobNumber"
                  label="Job Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cube-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  readonly
                ></v-text-field>
              </v-col>

              <!-- Seal Type -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.jobName"
                  label="Seal Type"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cube-outline"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Shaft Size -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.ShaftSize"
                  label="Shaft Size"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Customer Name -->
              <v-col cols="12" md="3" class="pa-2">
                <v-autocomplete
                  v-model="job.customerName"
                  :items="customers"
                  item-text="companyName"
                  item-value="companyName"
                  label="Customer Name"
                  dense
                  outlined
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-autocomplete>
              </v-col>

              <!-- Contact Person -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.customerNumber"
                  label="Contact Person"
                  dense
                  outlined
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Engineer -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.engineer"
                  :items="engineers"
                  label="Engineer"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Technician -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.technician"
                  :items="technicians"
                  label="Technician"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Salesperson -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.salesperson"
                  :items="salespersons"
                  label="Salesperson"
                  item-text="fullName"
                  item-value="docId"
                  outlined
                  dense
                  prepend-inner-icon="mdi-account"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Urgency (Priority) -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.urgency"
                  :items="urgencyOptions"
                  label="Priority"
                  dense
                  outlined
                  prepend-inner-icon="mdi-alert"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Set Rate -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model.number="job.setRate"
                  label="Set Rate"
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-usd"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Labour Hours -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model.number="job.timeToComplete"
                  label="Labour Hours"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.status"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  prepend-inner-icon="mdi-timeline-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  @change="handleStatusChange"
                  :disabled="statusChangeDisabled"
                ></v-select>
              </v-col>

              <!-- Current Date -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.currentDate"
                  label="Inspection Date"
                  dense
                  outlined
                  prepend-inner-icon="mdi-calendar"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Total Lead Time -->
              <v-col cols="12" md="3" class="pa-2">
                <v-select
                  v-model="job.TotalLead"
                  :items="leadTimeOptions"
                  label="Total Lead Time"
                  dense
                  outlined
                  prepend-inner-icon="mdi-clock"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-select>
              </v-col>

              <!-- Serial Number -->
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.serialNumber"
                  label="Serial Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-link"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Material Fields -->
            <v-row dense>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.elastomers"
                  label="Elastomers"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.ibs"
                  label="IBS Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.ibr"
                  label="IBR Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.obs"
                  label="OBS Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.obr"
                  label="OBR Face Material"
                  dense
                  outlined
                  prepend-inner-icon="mdi-cog-outline"
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>

              <!-- Conditional Fields -->
              <v-col cols="12" md="3" class="pa-2" v-if="showSalesOrderField">
                <v-text-field
                  v-model="job.salesOrderNumber"
                  label="Sales Order Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-file-document"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2" v-if="showQuoteNumberField">
                <v-text-field
                  v-model="job.quoteNumber"
                  label="Quote Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-file-document"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2" v-if="showInvoiceNumberField">
                <v-text-field
                  v-model="job.invoiceNumber"
                  label="Invoice Number"
                  dense
                  outlined
                  prepend-inner-icon="mdi-file-document"
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Job Description -->
            <v-row>
              <v-col cols="12" class="pa-2">
                <v-textarea
                  v-model="job.jobDescription"
                  label="Special Notes/Specific Requirements"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-list-bulleted"
                  class="montserrat-input"
                  :rules="[rules.required]"
                  style="min-height: 100px;"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Inspection Checklist -->
            <v-row class="pa-2">
              <v-btn dark color="blue" @click="openChecklistDialog" class="ma-2">
                <v-icon left>mdi-clipboard-check</v-icon>View Inspection Checklist
              </v-btn>
            </v-row>
            <v-row justify="end">
              <v-btn dark color="blue" @click="openAndDownloadPhotos" class="ma-2">
                <v-icon left>mdi-download</v-icon>Download All Photos
              </v-btn>
            </v-row>
            <display-checklist :jobNumber="job.jobNumber" v-model="checklistDialog" />

            <!-- Labour Items Section -->
            <div v-if="!hideLabourStock">
              <!-- Labour Items Table -->
              <v-row class="my-4">
                <h2 class="red--text mx-12">Labour Items</h2>
              </v-row>
              <v-simple-table class="pa-1 ma-0">
                <thead>
                  <tr>
                    <th class="text-left">Qty</th>
                    <th class="text-left">Part Size</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">Job Category</th>
                    <th class="text-left">Markup</th>
                    <th class="text-left">Unit Price</th>
                    <th class="text-left">Total Price</th>
                    <th class="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(labourItem, index) in job.labourItems" :key="index">
                    <td>
                      <v-text-field
                        v-model.number="labourItem.quantity"
                        type="number"
                        min="1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.positive]"
                        @input="calculateLabourTotalPrice(labourItem)"
                        style="width: 100px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.partSize"
                        type="number"
                        min="0"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.nonNegative]"
                        @input="calculateLabourTotalPrice(labourItem)"
                        style="width: 120px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="labourItem.description"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        :readonly="isInspection"
                        style="width: 400px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-select
                        v-model="labourItem.jobCategory"
                        :items="jobCategories.labour"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="setMarkup(labourItem)"
                        style="width: 300px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.markUp"
                        type="number"
                        min="0"
                        step="0.1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.nonNegative]"
                        @input="calculateLabourTotalPrice(labourItem)"
                        :readonly="labourItem.jobCategory !== 'Custom' || isInspection"
                        style="width: 80px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.unitPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        :readonly="isInspection"
                        style="width: 150px;"
                        @input="updateLabourTotalPrice(labourItem)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="labourItem.totalPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        readonly
                        style="width: 200px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        color="red"
                        @click="confirmAction('removeLabour', index)"
                        :disabled="isInspection || isSalesperson"
                        style="margin-bottom: 30px"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Add Labour Item Button and Subtotal -->
              <v-row justify="start" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  @click="confirmAction('addLabour')"
                  class="ma-2"
                  :disabled="isInspection || isSalesperson"
                >
                  <v-icon left>mdi-plus</v-icon>Add Labour Item
                </v-btn>
              </v-row>
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Labour Subtotal</h3>
                  <v-text-field
                    v-model.number="job.labourSubTotal"
                    label="Labour Subtotal"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Stock Items Section -->
              <v-row class="my-4">
                <h2 class="red--text mx-12">Stock/Parts Items</h2>
              </v-row>
              <v-simple-table class="pa-1 ma-0">
                <thead>
                  <tr>
                    <th class="text-left">Qty</th>
                    <th class="text-left">Code</th>
                    <th class="text-left">Additional Information</th>
                    <th class="text-left">Lead Time</th>
                    <th class="text-left">Stock Item</th>
                    <th class="text-left">Cost Price</th>
                    <th class="text-left">Markup</th>
                    <th class="text-left">Unit Price</th>
                    <th class="text-left">Total Price</th>
                    <th class="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(stockItem, index) in job.stockItems" :key="index">
                    <td>
                      <v-text-field
                        v-model.number="stockItem.quantity"
                        type="number"
                        min="1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.positive]"
                        @input="calculateStockTotalPrice(stockItem)"
                        style="width: 100px;"
                        :readonly="isInspection"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model="stockItem.code"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        :readonly="isInspection"
                        style="width: 150px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        dark
                        color="blue"
                        @click="openSupplierEditDialog(index)"
                        style="margin-bottom: 30px"
                      >
                        <v-icon>mdi-pencil</v-icon>Edit
                      </v-btn>
                    </td>
                    <td>
                      <v-select
                        v-model="stockItem.leadTime"
                        :items="leadTimeOptions"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="calculateStockTotalPrice(stockItem)"
                        style="width: 150px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-select
                        v-model="stockItem.jobCategory"
                        :items="jobCategories.stock"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required]"
                        @change="setMarkup(stockItem)"
                        style="width: 250px;"
                        :readonly="isInspection"
                      ></v-select>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.costPrice"
                        type="number"
                        min="0"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.nonNegative]"
                        @input="calculateStockTotalPrice(stockItem)"
                        :readonly="isInspection"
                        style="width: 120px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.markUp"
                        type="number"
                        min="0"
                        step="0.1"
                        dense
                        outlined
                        class="montserrat-input"
                        :rules="[rules.required, rules.nonNegative]"
                        @input="calculateStockTotalPrice(stockItem)"
                        :readonly="stockItem.jobCategory !== 'Custom' || isInspection"
                        style="width: 100px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.unitPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        :readonly="isInspection"
                        style="width: 120px;"
                        @input="updateStockTotalPrice(stockItem)"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="stockItem.totalPrice"
                        type="number"
                        dense
                        outlined
                        class="montserrat-input"
                        readonly
                        style="width: 150px;"
                      ></v-text-field>
                    </td>
                    <td>
                      <v-btn
                        icon
                        color="red"
                        @click="confirmAction('removeStock', index)"
                        :disabled="isInspection || isSalesperson"
                        style="margin-bottom: 30px"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Add Stock Item Button and Subtotal -->
              <v-row justify="start" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  @click="confirmAction('addStock')"
                  class="ma-2"
                  :disabled="isInspection || isSalesperson"
                >
                  <v-icon left>mdi-plus</v-icon>Add Stock/Part Item
                </v-btn>
              </v-row>
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Stock/Parts Subtotal</h3>
                  <v-text-field
                    v-model.number="job.stockSubTotal"
                    label="Stock/Parts Subtotal"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Grand Total -->
              <v-row justify="end">
                <v-col cols="12" md="3" class="pa-2">
                  <h3 class="section-title montserrat-title">Grand Total</h3>
                  <v-text-field
                    v-model.number="job.grandTotal"
                    label="Grand Total"
                    dense
                    outlined
                    prepend-inner-icon="mdi-cash-multiple"
                    class="montserrat-input"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- Supplier Quotes Section -->
            <h3 class="section-title montserrat-title">Supplier Quotes</h3>
            <v-row class="pa-2">
              <v-col
                cols="12"
                v-for="(quote, index) in job.extraInfoFields"
                :key="`quote-${index}`"
                class="pa-2"
              >
                <div class="supplier-quote-item">
                  <v-row>
                    <v-col cols="3" md="4">
                      <v-text-field
                        v-model="quote.text"
                        label="Quote Description"
                        outlined
                        dense
                        class="montserrat-input"
                        :rules="[rules.required]"
                        :readonly="isInspection"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="d-flex align-center">
                      <v-btn
                        icon
                        color="green"
                        @click="triggerQuoteFileUpload(index)"
                      >
                        <v-icon>mdi-upload</v-icon>
                      </v-btn>
                      <input
                        type="file"
                        :ref="'quoteFileInput' + index"
                        @change="handleQuoteFileUpload($event, index)"
                        style="display: none;"
                      />
                      <v-btn
                        icon
                        color="blue"
                        @click="downloadQuoteFile(quote.fileUrl)"
                        v-if="quote.fileUrl"
                      >
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                      <span class="ml-1">Download Doc</span>
                      <v-btn
                        icon
                        color="red"
                        @click="confirmAction('removeExtraField', index)"
                        :disabled="isInspection || isSalesperson"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>

            <!-- Add Supplier Quote Button -->
            <v-row style="margin-left: 50px" class="pa-2">
              <v-btn
                dark
                color="blue"
                @click="confirmAction('addQuote')"
                class="ma-2"
                :disabled="isInspection || isSalesperson"
              >
                <v-icon left>mdi-plus</v-icon>Add Supplier Quote
              </v-btn>
            </v-row>

            <!-- Journal Fields Section -->
            <div v-if="job.status !== 'Inspection'">
              <h3 class="section-title montserrat-title">Journal Fields</h3>
              <v-row class="pa-2">
                <v-col
                  cols="12"
                  v-for="(field, index) in job.customFields"
                  :key="`custom-field-${index}`"
                  class="pa-2"
                >
                  <div class="journal-field-item">
                    <v-row>
                      <v-col cols="3" md="4">
                        <v-text-field
                          v-model="field.value"
                          label="Journal Description"
                          outlined
                          dense
                          class="montserrat-input"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="2" class="d-flex align-center">
                        <v-btn
                          icon
                          color="red"
                          @click="confirmAction('removeCustomField', index)"
                          :disabled="isInspection || isSalesperson"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>

              <!-- Add Journal Field Button -->
              <v-row style="margin-left: 50px" class="pa-2">
                <v-btn
                  dark
                  color="blue"
                  @click="confirmAction('addCustomField')"
                  class="ma-2"
                  :disabled="!(job.status === 'InProgress' || job.status === 'Ready')"
                >
                  <v-icon left>mdi-plus</v-icon>Add Journal Field
                </v-btn>
              </v-row>
            </div>

            <!-- Additional Fields Section -->
            <v-row>
              <v-col cols="12" md="12" class="pa-2">
                <h3 class="section-title montserrat-title">Additional Fields</h3>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.sampleNumber"
                  label="Sample Number (Optional)"
                  outlined
                  dense
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.customerReference"
                  label="Customer Reference (Optional)"
                  outlined
                  dense
                  class="montserrat-input"
                  :readonly="isInspection"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <v-text-field
                  v-model="job.dateQuoted"
                  label="Date Quoted (Auto)"
                  outlined
                  dense
                  class="montserrat-input"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Comment and Summary -->
            <v-row class="pa-2">
              <v-col cols="12" md="12" class="pa-2" v-if="!isSalesperson">
                <v-textarea
                  v-model="job.comment"
                  label="Comment For Internal WorkShop"
                  outlined
                  dense
                  prepend-inner-icon="mdi-comment-text-outline"
                  class="montserrat-input"
                  style="min-height: 100px;"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row class="pa-2">
              <v-col cols="12" md="12" class="pa-2">
                <v-textarea
                  v-model="job.JobSummary"
                  label="Summary (Short Description to Send to Customer)"
                  outlined
                  dense
                  prepend-inner-icon="mdi-comment-text-outline"
                  class="montserrat-input"
                  style="min-height: 100px;"
                  :readonly="isInspection"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Save Button -->
            <v-row justify="center" class="my-6">
              <v-btn
                color="blue"
                large
                @click="confirmAction('saveJobDetails')"
                class="montserrat-button"
              >
                <v-icon left>mdi-content-save</v-icon>Save Changes
              </v-btn>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-state">
      <v-progress-circular indeterminate color="blue"></v-progress-circular>
      <span class="ml-2">Loading...</span>
    </div>

    <!-- Photo Carousel Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">Job Photos</v-card-title>
        <v-card-text>
          <v-carousel v-if="!loadingPhotos && jobPhotos.length > 0" hide-delimiters>
            <v-carousel-item v-for="(photo, index) in jobPhotos" :key="index">
              <v-img :src="photo" class="carousel-photo" max-height="400px"></v-img>
            </v-carousel-item>
          </v-carousel>
          <div v-if="loadingPhotos" class="d-flex align-center">
            <v-progress-circular indeterminate color="blue"></v-progress-circular>
            <span class="ml-2">Loading photos...</span>
          </div>
          <div v-if="!loadingPhotos && jobPhotos.length === 0">
            <p>No photos available for this job.</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" dark @click="downloadPhotos" :disabled="isInspection || isSalesperson">
            <v-icon left>mdi-download</v-icon>Download All Photos
          </v-btn>
          <v-btn color="red" dark @click="photoDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Edit Dialog -->
    <v-dialog v-model="supplierEditDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Edit Supplier Details</v-card-title>
        <v-card-text>
          <v-form ref="supplierForm" v-model="supplierFormValid">
            <v-autocomplete
              v-model="editSupplier.supplier"
              :items="suppliers"
              item-text="supplierName"
              item-value="docId"
              label="Supplier"
              dense
              outlined
              prepend-inner-icon="mdi-factory"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-autocomplete>

            <v-text-field
              dense
              outlined
              label="Drawing Number"
              v-model="editSupplier.drawingNumber"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-text-field>

            <v-textarea
              dense
              outlined
              label="Special Note"
              v-model="editSupplier.specialNote"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isInspection"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dark @click="saveSupplierDetails" :disabled="isInspection">
            Save
          </v-btn>
          <v-btn color="grey" dark @click="supplierEditDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog for Actions -->
    <v-dialog v-model="confirmationDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>{{ confirmationMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="executeConfirmedAction">Yes</v-btn>
          <v-btn color="red darken-1" text @click="confirmationDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Extra Field Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this item and associated photo?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dark @click="deleteFieldConfirmed">Delete</v-btn>
          <v-btn color="grey" dark @click="deleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- File Preview Dialog -->
    <v-dialog v-model="fileDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">File Preview</v-card-title>
        <v-card-text>
          <v-img :src="selectedFileUrl" max-width="100%"></v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dark @click="fileDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Back Confirmation Dialog -->
    <v-dialog v-model="backDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dark @click="goBack">Yes</v-btn>
          <v-btn color="grey" dark @click="backDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="successSnack.show" :timeout="3000" color="green" top right>
      {{ successSnack.message }}
      <v-btn text @click="successSnack.show = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnack.show" :timeout="3000" color="red" top right>
      {{ errorSnack.message }}
      <v-btn text @click="errorSnack.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";
import DisplayChecklist from "@/components/DisplayChecklist.vue";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  components: { DisplayChecklist },
  data() {
    return {
      job: null,
      checklistFilled: false,
      jobPhotos: [],
      extraInfoFields: [],
      customFields: [],
      deleteDialog: false,
      fieldToDeleteIndex: null,
      fileDialog: false,
      selectedFileUrl: null,
      supplierEditDialog: false,
      editSupplier: {
        index: null,
        supplier: null,
        drawingNumber: "",
        specialNote: "",
      },
      supplierFormValid: false,
      confirmationDialog: false,
      confirmedAction: null,
      confirmationMessage: "",
      customers: [],
      suppliers: [],
      engineers: [],
      technicians: [],
      salespersons: [],
      photoDialog: false,
      loadingPhotos: false,
      loading: true,
      checklistDialog: false,
      backDialog: false,
      valid: false,
      hasChanges: false,
      currentUser: null,

      rules: {
        required: (v) => !!v || "Required.",
        positive: (v) => v > 0 || "Must be positive.",
        nonNegative: (v) => v >= 0 || "Cannot be negative.",
      },

      urgencyOptions: ["Urgent", "Medium", "Not Urgent"],
      leadTimeOptions: [
        "1-2 DAYS","2-3 DAYS","3-5 DAYS","4-6 DAYS","5-7 DAYS","6-8 DAYS",
        "8-10 DAYS","10-12 DAYS","10-15 DAYS","15-20 DAYS","6-8 WEEKS",
        "8-10 WEEKS","10-12 WEEKS","Ex - Stock","Custom",
      ],
      jobCategories: {
        stock: [
          "Bearings","Bolts/Nuts","Coating (Any)","Die Set","Gasket",
          "Grinding - no drawing","Lip Seal / V-seal (not in seal)",
          "Lip Seal / V-seal (in seal or pump repair)","Local Mods",
          "Machining - no drawing","Material","Nexus Drawn item",
          "O-Rings Only Quote","O-Rings with seal","Special Markup",
          "Special New","Stock Item","Tools","Custom",
        ],
        labour: [
          "Lapping Carbon","Lapping Sic/SSic/SS/Cer",
          "Lapping TC","Custom",
        ],
      },
      statuses: [
        "Inspection","Costing Complete","Quoted","InProgress",
        "Ready","Completed","Delayed","Canceled",
      ],
      isSalesperson: false,
      isTechnician: false,
      isAdmin: false,
      originalStatus: null,
      successSnack: { show: false, message: "" },
      errorSnack: { show: false, message: "" },
    };
  },
  watch: {
    job: {
      handler() { if (this.job) { this.calculateGrandTotal(); this.hasChanges = true; } },
      deep: true,
    },
  },
  async created() {
    await this.fetchCurrentUser();
    await this.fetchJobDetails();
    await Promise.all([
      this.fetchCustomers(),
      this.fetchSuppliers(),
      this.fetchEngineers(),
      this.fetchTechnicians(),
      this.fetchSalespersons(),
    ]);
    this.checkUserRole();
    this.calculateGrandTotal();
    await this.fetchChecklistForJob();
  },
  mounted() {
    this.checkTabletMode();
    window.addEventListener("resize", this.checkTabletMode);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkTabletMode);
  },
  methods: {
    async fetchCurrentUser() {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) this.currentUser = userDoc.data();
        } catch (error) { console.error("Error fetching user:", error); }
      }
    },
    async fetchChecklistForJob() {
      if (!this.job || !this.job.jobNumber) return;
      try {
        const snapshot = await getDocs(collection(db, "checklists"));
        this.checklistFilled = snapshot.docs.some(
          (d) => d.data().jobNumber === this.job.jobNumber
        );
      } catch (e) {
        console.error("Error fetching checklist:", e);
        this.errorSnack.message = "Error checking checklist.";
        this.errorSnack.show = true;
      }
    },
    checkTabletMode() { /* add if needed */ },
    selectAllText(e) { e.target.select(); },
    addQuote() { this.job.extraInfoFields.push({ text: "", fileUrl: null }); this.hasChanges = true; },
    triggerQuoteFileUpload(index) {
      this.$refs['quoteFileInput' + index][0].click();
    },
    async handleQuoteFileUpload(event, index) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const storageRef = ref(
          storage,
          `jobs/${this.job.jobNumber}/supplierQuotes/${Date.now()}_${file.name}`
        );
        await uploadBytes(storageRef, file, { contentType: file.type, metadata: { contentDisposition: 'attachment' } });
        const url = await getDownloadURL(storageRef);
        this.$set(this.job.extraInfoFields[index], 'fileUrl', url);
        this.successSnack.message = 'File uploaded.';
        this.successSnack.show = true;
        await this.saveJobDetails();
      } catch (e) {
        console.error('Upload error:', e);
        this.errorSnack.message = 'Failed to upload file.';
        this.errorSnack.show = true;
      }
    },
    viewFile(i) {
      const url = this.job.extraInfoFields[i].fileUrl;
      if (url) { this.selectedFileUrl = url; this.fileDialog = true; }
      else { this.errorSnack.message = "No file available."; this.errorSnack.show = true; }
    },
    downloadQuoteFile(fileUrl) {
      if (!fileUrl) return;
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    confirmDeleteField(i) { this.fieldToDeleteIndex = i; this.deleteDialog = true; },
    async deleteFieldConfirmed() {
      const idx = this.fieldToDeleteIndex;
      const field = this.job.extraInfoFields[idx];
      if (field && field.fileUrl) {
        try {
          const filePath = decodeURIComponent(field.fileUrl.split(storage._prefix).pop().split("?")[0]);
          await deleteObject(ref(storage, filePath));
          this.successSnack.message = "File deleted.";
          this.successSnack.show = true;
        } catch (e) {
          console.error("Delete file error:", e);
          this.errorSnack.message = "Failed to delete file.";
          this.errorSnack.show = true;
        }
      }
      this.job.extraInfoFields.splice(idx, 1);
      this.hasChanges = true;
      await this.saveJobDetails();
      this.deleteDialog = false;
      this.fieldToDeleteIndex = null;
    },
    openSupplierEditDialog(i) {
      const s = this.job.stockItems[i];
      this.editSupplier = { index: i, supplier: s.supplier, drawingNumber: s.drawingNumber, specialNote: s.specialNote };
      this.supplierEditDialog = true;
    },
    async saveSupplierDetails() {
      if (!this.$refs.supplierForm.validate()) {
        this.errorSnack.message = "Please fill required fields.";
        this.errorSnack.show = true; return;
      }
      const { index, supplier, drawingNumber, specialNote } = this.editSupplier;
      Object.assign(this.job.stockItems[index], { supplier, drawingNumber, specialNote });
      this.hasChanges = true;
      await this.saveJobDetails();
      this.supplierEditDialog = false;
      this.successSnack.message = "Supplier updated.";
      this.successSnack.show = true;
    },
    async fetchJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (!jobId) { this.errorSnack.message = "No job ID found."; this.errorSnack.show = true; return; }
      try {
        this.loading = true;
        const jobDoc = await getDoc(doc(db, "jobs", jobId));
        if (!jobDoc.exists()) { this.errorSnack.message = "No such job found."; this.errorSnack.show = true; return; }
        const d = jobDoc.data();
        this.job = {
          jobNumber: d.jobNumber || "", jobName: d.jobName || "", customerName: d.customerName || "",
          customerNumber: d.customerNumber || "", urgency: d.urgency || "", timeToComplete: d.timeToComplete || "",
          setRate: d.setRate || "", currentDate: d.currentDate || "", dateQuoted: d.dateQuoted || "",
          engineer: d.engineer || "", technician: d.technician || "", salesperson: d.salesperson || "",
          status: d.status || "", quoteNumber: d.quoteNumber || "", TotalLead: d.TotalLead || "",
          ShaftSize: d.ShaftSize || "", salesOrderNumber: d.salesOrderNumber || "", invoiceNumber: d.invoiceNumber || "",
          cancellationReason: d.cancellationReason || "", photos: d.photos || [], jobDescription: d.jobDescription || "",
          JobSummary: d.JobSummary || "", comment: d.comment || "", labourItems: d.labourItems || [],
          stockItems: d.stockItems || [], customFields: Array.isArray(d.customFields) ? d.customFields : [],
          labourSubTotal: d.labourSubTotal || 0, stockSubTotal: d.stockSubTotal || 0, grandTotal: d.grandTotal || 0,
          sampleNumber: d.sampleNumber || "", customerReference: d.customerReference || "", inventoryJournalNumber: d.inventoryJournalNumber || "",
          extraInfoFields: d.extraInfoFields || [], permissions: d.permissions || {},
          serialNumber: d.serialNumber || "", elastomers: d.elastomers || "", ibs: d.ibs || "",
          ibr: d.ibr || "", obs: d.obs || "", obr: d.obr || "",
        };
        this.jobPhotos = [...this.job.photos];
        this.extraInfoFields = [...this.job.extraInfoFields];
        this.customFields = [...this.job.customFields];
        this.originalStatus = this.job.status;
      } catch (e) {
        console.error("Fetch job error:", e);
        this.errorSnack.message = "Error fetching job.";
        this.errorSnack.show = true;
      } finally { this.loading = false; }
    },
    async fetchCustomers() {
      try {
        const cache = localStorage.getItem("customers");
        if (cache) this.customers = JSON.parse(cache);
        else {
          const snap = await getDocs(collection(db, "customers"));
          this.customers = snap.docs.map((d) => ({ docId: d.id, companyName: d.data()["Company Name"] || "" }))
            .sort((a, b) => a.companyName.localeCompare(b.companyName));
          localStorage.setItem("customers", JSON.stringify(this.customers));
        }
      } catch (e) { console.error("Fetch customers error:", e); }
    },
    async fetchSuppliers() {
      try {
        const cache = localStorage.getItem("suppliers");
        if (cache) this.suppliers = JSON.parse(cache);
        else {
          const snap = await getDocs(collection(db, "suppliers"));
          this.suppliers = snap.docs.map((d) => ({ docId: d.id, supplierName: d.data()["Supplier Name"] || "" }))
            .sort((a, b) => a.supplierName.localeCompare(b.supplierName));
          localStorage.setItem("suppliers", JSON.stringify(this.suppliers));
        }
      } catch (e) { console.error("Fetch suppliers error:", e); }
    },
    async fetchEngineers() {
      try {
        const cache = localStorage.getItem("engineers");
        if (cache) this.engineers = JSON.parse(cache);
        else {
          const snap = await getDocs(collection(db, "users"));
          this.engineers = snap.docs.map((d) => ({ docId: d.id, fullName: d.data().fullName, role: d.data().role }))
            .filter((u) => u.role === "Engineer")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("engineers", JSON.stringify(this.engineers));
        }
      } catch (e) { console.error("Fetch engineers error:", e); }
    },
    async fetchTechnicians() {
      try {
        const cache = localStorage.getItem("technicians");
        if (cache) this.technicians = JSON.parse(cache);
        else {
          const snap = await getDocs(collection(db, "users"));
          this.technicians = snap.docs.map((d) => ({ docId: d.id, fullName: d.data().fullName, role: d.data().role }))
            .filter((u) => u.role === "Technician")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("technicians", JSON.stringify(this.technicians));
        }
      } catch (e) { console.error("Fetch technicians error:", e); }
    },
    async fetchSalespersons() {
      try {
        const cache = localStorage.getItem("salespersons");
        if (cache) this.salespersons = JSON.parse(cache);
        else {
          const snap = await getDocs(collection(db, "users"));
          this.salespersons = snap.docs.map((d) => ({ docId: d.id, fullName: d.data().fullName, role: d.data().role }))
            .filter((u) => u.role === "Salesperson")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("salespersons", JSON.stringify(this.salespersons));
        }
      } catch (e) { console.error("Fetch salespersons error:", e); }
    },
    getLoggedInUserRole() { return this.currentUser ? this.currentUser.role : "Admin"; },
    checkUserRole() {
      const r = this.getLoggedInUserRole();
      this.isSalesperson = r === "Salesperson";
      this.isTechnician = r === "Technician";
      this.isAdmin = r === "Admin";
    },
    async handleStatusChange() {
      if (this.job.status === "Quoted" && !this.job.dateQuoted)
        this.job.dateQuoted = new Date().toISOString().split("T")[0];
      if (!this.showSalesOrderField) this.job.salesOrderNumber = "";
      if (!this.showInvoiceNumberField) this.job.invoiceNumber = "";
      if (!this.showReasonField) this.job.cancellationReason = "";
      if (!this.showQuoteNumberField) this.job.quoteNumber = "";
      this.calculateGrandTotal(); this.hasChanges = true;
    },
    openChecklistDialog() {
      if (this.job && this.job.jobNumber) this.checklistDialog = true;
      else { this.errorSnack.message = "Job number not available."; this.errorSnack.show = true; }
    },
    confirmAction(action, index = null) {
      this.confirmedAction = { action, index };
      const m = {
        removeLabour: "Remove this labour item?",
        removeStock: "Remove this stock item?",
        removeCustomField: "Remove this journal field?",
        removeExtraField: "Remove this supplier quote?",
        addLabour: "Add a labour item?",
        addStock: "Add a stock/part item?",
        addCustomField: "Add a journal field?",
        addQuote: "Add a supplier quote?",
        saveJobDetails: "Save the changes?",
      };
      this.confirmationMessage = m[action] || "Proceed?";
      this.confirmationDialog = true;
    },
    async executeConfirmedAction() {
      const { action, index } = this.confirmedAction;
      switch (action) {
        case "removeLabour": this.job.labourItems.splice(index, 1); break;
        case "removeStock": this.job.stockItems.splice(index, 1); break;
        case "removeCustomField": this.job.customFields.splice(index, 1); break;
        case "removeExtraField": await this.deleteFieldConfirmed(); return;
        case "addLabour":
          this.job.labourItems.push({ quantity: 1, description: "", partSize: 0, markUp: 1, unitPrice: 0, totalPrice: 0, jobCategory: "" }); break;
        case "addStock":
          this.job.stockItems.push({ quantity: 1, code: "", drawingNumber: "", supplier: "", leadTime: "", costPrice: 0, markUp: 1, unitPrice: 0, totalPrice: 0, jobCategory: "" }); break;
        case "addCustomField": this.job.customFields.push({ value: "" }); break;
        case "addQuote": this.addQuote(); break;
        case "saveJobDetails": await this.saveJobDetails(); break;
      }
      this.confirmationDialog = false;
      this.calculateGrandTotal();
      this.hasChanges = true;
    },
    async saveJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (!jobId) { this.errorSnack.message = "No job ID."; this.errorSnack.show = true; return; }
      this.loading = true;
      try {
        await updateDoc(doc(db, "jobs", jobId), {
          ...this.job,
          quoteNumber: this.showQuoteNumberField ? this.job.quoteNumber : null,
          salesOrderNumber: this.showSalesOrderField ? this.job.salesOrderNumber : null,
          invoiceNumber: this.showInvoiceNumberField ? this.job.invoiceNumber : null,
          cancellationReason: this.showReasonField ? this.job.cancellationReason : null,
        });
        this.successSnack.message = "Job updated.";
        this.successSnack.show = true;
        this.originalStatus = this.job.status;
        this.hasChanges = false;
      } catch (e) {
        console.error("Update job error:", e);
        this.errorSnack.message = "Error updating job.";
        this.errorSnack.show = true;
      } finally { this.loading = false; }
    },
    async fetchChecklistPhotos(jobNumber) {
      const checklistRef = collection(db, "checklists");
      const q = query(checklistRef, where("jobNumber", "==", jobNumber));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return [];
      const checklist = querySnapshot.docs[0].data().checklist || [];
      return checklist.flatMap(section => section.photos || []);
    },
    async openPhotoDialog() {
      if (!this.job.jobNumber) {
        this.errorSnack.message = "No job number.";
        this.errorSnack.show = true;
        return;
      }
      this.photoDialog = true;
      this.loadingPhotos = true;
      try {
        this.jobPhotos = await this.fetchChecklistPhotos(this.job.jobNumber);
      } catch (e) {
        console.error("Fetch checklist photos error:", e);
        this.errorSnack.message = "Error fetching checklist photos.";
        this.errorSnack.show = true;
      } finally {
        this.loadingPhotos = false;
      }
    },
    async openAndDownloadPhotos() {
      try {
        await this.openPhotoDialog();
        if (this.jobPhotos.length) this.downloadPhotos();
      } catch (e) {
        console.error(e);
        this.errorSnack.message = "Failed to download photos.";
        this.errorSnack.show = true;
      }
    },
    async downloadPhotos() {
      if (!this.jobPhotos.length) {
        this.errorSnack.message = "No photos.";
        this.errorSnack.show = true;
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(`job_${this.job.jobNumber}_photos`);
      await Promise.all(this.jobPhotos.map((url, i) =>
        fetch(url).then((r) => r.blob()).then((b) => {
          folder.file(`photo_${i + 1}_${url.split("/").pop().split("?")[0]}`, b);
        })
      ));
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `job_${this.job.jobNumber}_photos.zip`);
      this.successSnack.message = "Photos downloaded.";
      this.successSnack.show = true;
    },
    handleItemChange() { this.calculateGrandTotal(); this.hasChanges = true; },
    calculateLabourTotalPrice(item) {
      const quantity = +item.quantity || 0;
      const markUp = +item.markUp || 0;
      const unitPrice = +item.unitPrice || 0;
      const partSize = +item.partSize || 0;
      let isLapping = ["Lapping Carbon", "Lapping Sic/SSic/SS/Cer", "Lapping TC"].includes(item.jobCategory);

      if (item.jobCategory === "Custom") {
        item.totalPrice = quantity * unitPrice;
        if (isLapping) {
          item.unitPrice = Math.ceil(unitPrice);
          item.totalPrice = Math.ceil(item.totalPrice);
        }
      } else {
        item.unitPrice = partSize * markUp;
        item.totalPrice = quantity * item.unitPrice;
        if (isLapping) {
          item.unitPrice = Math.ceil(item.unitPrice);
          item.totalPrice = Math.ceil(item.totalPrice);
        }
      }
      this.calculateGrandTotal();
      this.hasChanges = true;
    },
    updateLabourTotalPrice(item) {
      if (item.jobCategory === "Custom") { item.totalPrice = (+item.quantity || 0) * item.unitPrice;
        this.calculateGrandTotal(); this.hasChanges = true; }
    },
    calculateStockTotalPrice(item) {
      const q = +item.quantity || 0, cp = +item.costPrice || 0, m = +item.markUp || 0;
      if (!item.costPrice) item.totalPrice = q * item.unitPrice;
      else {
        if (item.jobCategory !== "Custom") item.unitPrice = cp * m;
        item.totalPrice = q * item.unitPrice;
      }
      this.calculateGrandTotal(); this.hasChanges = true;
    },
    updateStockTotalPrice(item) { if (item.jobCategory === "Custom") {
        item.totalPrice = (+item.quantity || 0) * item.unitPrice;
        this.calculateGrandTotal(); this.hasChanges = true;
      }
    },
    calculateGrandTotal() {
      const labour = this.job.labourItems.reduce((a, i) => {
        let isLapping = ["Lapping Carbon", "Lapping Sic/SSic/SS/Cer", "Lapping TC"].includes(i.jobCategory);
        let total = +i.totalPrice || 0;
        return a + (isLapping ? Math.ceil(total) : total);
      }, 0);
      const addLab = (+this.job.setRate || 0) * (+this.job.timeToComplete || 0);
      this.job.labourSubTotal = Math.ceil(labour + addLab);
      this.job.stockSubTotal = this.job.stockItems.reduce((a, i) => a + (+i.totalPrice || 0), 0);
      this.job.grandTotal = Math.ceil(this.job.labourSubTotal + this.job.stockSubTotal);
    },
    setMarkup(item) {
      if (item.jobCategory === "Custom") item.markUp = 1;
      else {
        const map = {
          "Lapping Carbon": 4, "Lapping Sic/SSic/SS/Cer": 4.5, "Lapping TC": 5.5, Bearings: 1.1,
          "Bolts/Nuts": 1.5, "Coating (Any)": 1.1, "Die Set": 1.1, Gasket: 1.5,
          "Grinding - no drawing": 1.5, "Lip Seal / V-seal (not in seal)": 1.5,
          "Lip Seal / V-seal (in seal or pump repair)": 1.1, "Local Mods": 1.5,
          "Machining - no drawing": 1.5, Material: 1, "Nexus Drawn item": 1.8,
          "O-Rings Only Quote": 3, "O-Rings with seal": 1.5, "Special Markup": 1.5,
          "Special New": 1.3, "Stock Item": 1.2, Tools: 1.1,
        };
        item.markUp = map[item.jobCategory] || 1.1;
      }
      this.handleItemChange();
    },
    openBackConfirmation() { this.backDialog = true; },
    goBack() { this.backDialog = false; this.$router.go(-1); },
  },
  computed: {
    isInspection() { return this.job && this.job.status !== "Inspection"; },
    statusChangeDisabled() {
      return this.job && this.job.status === "Inspection" && !this.checklistFilled;
    },
    showQuoteNumberField() { return this.job.status === "Quoted"; },
    showSalesOrderField() { return ["InProgress","Ready","Delayed"].includes(this.job.status); },
    showInvoiceNumberField() { return this.job.status === "Completed"; },
    showReasonField() { return this.job.status === "Canceled"; },
    canChangeStatus() { return this.isAdmin; },
    statusOptions() {
      return this.statuses.map((s, idx) => {
        let d = false;
        if (this.job && this.job.status === "Inspection" && !this.checklistFilled)
          d = s !== "Inspection";
        else if (!this.isAdmin) {
          const cur = this.statuses.indexOf(this.originalStatus);
          if (s === this.originalStatus) d = false;
          else if (idx === cur + 1) {
            if (this.originalStatus === "Inspection" && s === "Costing Complete" && !this.checklistFilled) d = true;
            else d = false;
          } else if (["Canceled","Delayed"].includes(s)) d = false;
          else d = true;
        }
        return { text: s, value: s, disabled: d };
      });
    },
    hideLabourStock() {
      return (
        this.currentUser &&
        this.currentUser.jobs &&
        this.currentUser.jobs["View_Line_Items_(Labour_and_Stock)"] === true
      );
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

.view-job-container { background: #000000; min-height: 100vh; position: relative; width: 100%; }
.back-button { color: #fff; background: red; border-radius: 50%; position: absolute; top: 16px; left: 16px; transition: transform .3s; }
.back-button:hover { transform: scale(1.1); }
.add-job-card { background: #fff; border-radius: 8px; padding: 20px; box-shadow: 0 4px 20px rgba(0,0,0,.2); }
.section-title { font-size: 20px; font-weight: bold; margin: 22px 0 16px; color: red; }
.montserrat-input { font-family: "Montserrat", sans-serif; font-weight: 400; }
.montserrat-button { font-family: "Montserrat", sans-serif; font-weight: 700; }
.montserrat-title { font-family: "Montserrat", sans-serif; font-weight: 700; }
.v-simple-table th { background: #f5f5f5; color: #000; padding: 12px; }
.v-simple-table td { padding: 12px; }
@media (max-width: 600px) {.add-job-card {padding: 10px;} .montserrat-input,.montserrat-button {font-size: 14px;}}
.supplier-quote-item,.journal-field-item { padding: 16px; border: 1px solid #e0e0e0; border-radius: 8px; background: #fafafa; }
.supplier-quote-item+.supplier-quote-item,.journal-field-item+.journal-field-item { margin-top: 16px; }
.d-flex { display: flex; }
.align-center { align-items: center; }
.ml-2 { margin-left: 8px; }
.v-snackbar { z-index: 1000; }
.carousel-photo { object-fit: contain; }
.loading-state { display: flex; justify-content: center; align-items: center; height: 80vh; flex-direction: column; }
.supplier-quote-item,.journal-field-item { display: flex; flex-direction: column; }
</style>